import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import Page from '../../../components/Page.js';
import Grid from '@material-ui/core/Grid';
import Container from '@material-ui/core/Container';
import Typography from '@material-ui/core/Typography';
import TabsPanel from '../components//TabsPanel.js';
import FilterPanel from '../components/FilterPanel.js';
import FilterDisplay from '../components//FilterDisplay.js';
import SectionTitle from '../../../components/SectionTitle.js';
import { fetchData } from '../components//FetchData.js';
import * as QueryString from 'query-string';
import { useHistory } from 'react-router-dom';
import { ProviderAPI } from '../../../api';
import * as track from '../../../lib/track';

const LIMIT = 10;

const scrollToRef = (ref) => window.scrollTo(0, ref.current.offsetTop);

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    flexWrap: 'wrap',
    alignContent: 'flex-start',
    padding: '2rem 1rem',
    minHeight: '65vh',
  },
  title: {
    fontSize: '1.125rem',
    fontWeight: 500,
  }
}));

const defaultState = {
  isLoading: true,
  data: [],
  total: 0,
  pageCount: 1,
  currentPage: 1,
  paginationText: '',
};

const SearchPage = ({location}) => {
  let history = useHistory();
  const classes = useStyles();
  const scrollRef = React.useRef(null);
  const executeScroll = () => scrollToRef(scrollRef);

  const [providers, setProviders] = React.useState(defaultState);
  const [listings, setListings] = React.useState(defaultState);

  const urlParams = QueryString.parse(location.search);
  const tab = ('tab' in urlParams) ? parseInt(urlParams.tab) : null;
  const page = ('page' in urlParams) ? parseInt(urlParams.page) : 1;

  const getData = tab => {
    // uses 1-indexed tab number
    switch(tab) {
    case 1:
      fetchData(urlParams, ProviderAPI.searchListings, setListings, page, LIMIT);
      break;
    case 2:
      fetchData(urlParams, ProviderAPI.searchProfiles, setProviders, page, LIMIT);
      break;
    default:
      fetchData(urlParams, ProviderAPI.searchProfiles, setProviders, page, LIMIT);
      fetchData(urlParams, ProviderAPI.searchListings, setListings, page, LIMIT);
    }
  };

  const pushSearch = (searchParams) => {
    const query = new URLSearchParams(searchParams).toString();
    history.push(`/search?${query}`);
    executeScroll();
  };

  const pushQuery = (key, value) => {
    if (!(key in urlParams)) {
      delete urlParams['page']; // go to page 1 on any change other than page
    }
    if (value !== null) {
      urlParams[key] = value;
      pushSearch(urlParams);
    }
  };

  const onTabChange = (event, value) => {
    const selectedTab = value + 1;
    delete urlParams['page'];
    pushQuery('tab', selectedTab);
    track.sendEvent('search page tab', 'click', {value: selectedTab});
  };

  const onPageChange = (event, page) => {
    pushQuery('page', page);
    track.sendEvent('search result page', 'change', {value: page});
  };

  const filterClick = (key, value) => {
    pushQuery(key, value);
    track.sendEvent('search filter', 'select', {key: key, value: value});
  };

  const filterClose = (key) => {
    delete urlParams[key];
    pushSearch(urlParams);
    track.sendEvent('search filter', 'close', {key: key});
  };

  const searchSubmit = (value) => {
    pushQuery('search_text', value);
    track.sendEvent('search text input', 'submit', {value: value});
  };

  const setLocation = (value) => {
    pushQuery('service_location', value);
    track.sendEvent('search location input', 'submit', {value: value});
  };

  const makeSubtitle = () => {
    const serviceText = ('service' in urlParams) ? `${urlParams.service} ` : 'all ';
    const locText = ('service_location' in urlParams) ? ` delivered in ${urlParams.service_location}` : '';
    const searchText = ('search_text' in urlParams) ? ` mentioning '${urlParams.search_text}'` : '';
    const ndisRegText = ('ndis_registered' in urlParams) ? ' that are NDIS registered' : '';
    const ndisecRegText = ('ndisec_registered' in urlParams) ? ' and are NDIS Early Childhood registered' : '';
    const specText = ('specialisation' in urlParams) ? ` specialising in ${urlParams.specialisation}` : '';
    const ageText = ('age_group' in urlParams) ? ` for ${urlParams.age_group}` : '';
    const accessText = ('access_method' in urlParams) ? ` accessed by ${urlParams.access_method}` : '';
    return `Find ${serviceText}services${locText}${searchText}${ndisRegText}${ndisecRegText}${specText}${ageText}${accessText}.`;
  };

  const totalResults = listings.total + providers.total;
  const subtitle = `Found ${totalResults} results.`;
  const title = makeSubtitle(urlParams);

  let tabValue = 0;
  if (!listings.isLoading && !providers.isLoading && tab === null) {
    tabValue = (listings.total > 0) ? 0 : 1;
  } else if (tab !== null) {
    tabValue = tab - 1;
  }

  React.useEffect(() => {
    if (!('page' in urlParams)) {
      getData(null);
    } else {
      getData(tab);
    }
  }, [location]); // eslint-disable-line

  React.useEffect(() => {
    if (!(listings.isLoading) && tabValue === 0) {
      const providerUuids = listings.data.map(item => item.provider_uuid);
      const listingUuids = listings.data.map(item => item.uuid);
      track.sendEvent('listing search results', 'view', {
        page: page,
        listing_uuids: listingUuids,
      }, providerUuids);
    }
  }, [listings]); // eslint-disable-line

  React.useEffect(() => {
    if (!(providers.isLoading) && tabValue === 1) {
      const providerUuids = listings.data.map(item => item.provider_uuid);
      track.sendEvent('provider search results', 'view', {
        page: page,
      }, providerUuids);
    }
  }, [providers]); // eslint-disable-line

  return (
    <Page title={title}>
      <div className={classes.root}>
        <Container maxWidth="lg" fixed disableGutters={true}>
          <Grid container spacing={3}>
            <Grid item md={3}>
              <FilterPanel
                filterClick={filterClick}
                filterClose={filterClose}
                searchSubmit={searchSubmit}
                params={urlParams}
                setLocation={setLocation}
              />
            </Grid>
            <Grid item md={9} ref={scrollRef}>
              <SectionTitle title={title} variant="h1" className={classes.title}/>
              <Typography variant="subtitle2">{subtitle}</Typography>
              <FilterDisplay
                params={urlParams}
                filterClose={filterClose}
              />
              <TabsPanel
                providers={providers}
                listings={listings}
                onPageChange={onPageChange}
                selectedTab={tabValue}
                onTabChange={onTabChange}
              />
            </Grid>
          </Grid>
        </Container>
      </div>
    </Page>
  );

};

SearchPage.propTypes = {
  location: PropTypes.object.isRequired,
};

export default SearchPage;
